import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { AccessEditor } from './AccessEditor';
import { useAccount } from '../../contexts/AccountContext';
import { useToast } from '../../contexts/ToastContext';
import { normalizeAccess, type RoleGrant } from '../../domain/access';
import { toUserErrorMessage } from '../../lib/apiError';
import { useInviteMember } from '../../services/api/hooks/useInviteMember';
import { FormField } from '../common/FormField';
import { IconX } from '../incidents/icons';

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
}

const schema = z.object({
  firstName: z.string().trim().min(1, { message: 'Prénom requis' }).max(100),
  lastName: z.string().trim().min(1, { message: 'Nom requis' }).max(100),
  email: z.string().trim().email({ message: 'Email invalide' }),
});

type InviteFormValues = z.infer<typeof schema>;

/**
 * `POST /v1/invitation/invite`. La validation est faite ici : le 400 du back
 * (dump class-validator) n'est pas affichable.
 */
export const InviteMemberModal = ({ open, onClose }: InviteMemberModalProps) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center px-4">
      <div
        onClick={onClose}
        aria-hidden
        className="absolute inset-0 bg-black/30 drawer-backdrop-in"
        style={{ backdropFilter: 'blur(2px)' }}
      />
      <InviteMemberForm onClose={onClose} />
    </div>
  );
};

const InviteMemberForm = ({ onClose }: { onClose: () => void }) => {
  const toast = useToast();
  const invite = useInviteMember();
  const { isAdmin, access: ownAccess } = useAccount();
  // Un membre non-admin invite « quelqu'un comme lui » : on part de son propre
  // accès, qu'il peut ensuite restreindre. Un admin part d'une page blanche,
  // il attribue ce qu'il veut.
  const [access, setAccess] = useState<RoleGrant[]>(() => (isAdmin ? [] : normalizeAccess(ownAccess)));
  const [accessTouched, setAccessTouched] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { firstName: '', lastName: '', email: '' },
  });

  const accessError = accessTouched && access.length === 0 ? 'Sélectionnez au moins un profil.' : null;

  const onSubmit: SubmitHandler<InviteFormValues> = values => {
    setAccessTouched(true);
    if (access.length === 0) return;
    invite.mutate(
      { ...values, access },
      {
        onSuccess: ({ outcome }) => {
          // v1.9.0 : un utilisateur déjà présent sur le compte reçoit l'accès
          // sans email. Annoncer un envoi dans ce cas serait faux.
          if (outcome === 'ACCESS_GRANTED') {
            toast.success(
              'Accès accordé',
              `${values.email} avait déjà un compte : l'accès est actif immédiatement, sans email.`,
            );
          } else {
            toast.success('Invitation envoyée', `Un email a été envoyé à ${values.email}.`);
          }
          onClose();
        },
        onError: err => {
          const { title, description } = toUserErrorMessage(err, "Échec de l'invitation");
          toast.error(title, description);
        },
      },
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit, () => setAccessTouched(true))}
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-member-title"
      className="relative w-full max-w-lg bg-white border border-zinc-200 rounded-md shadow-lg drawer-panel-in flex flex-col max-h-[85vh]"
    >
      <header className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between gap-4">
        <h2 id="invite-member-title" className="text-sm font-medium text-zinc-900">
          Inviter un membre
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="h-8 w-8 inline-flex items-center justify-center rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
        >
          <IconX width={16} height={16} />
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            id="invite-first-name"
            label="Prénom"
            autoComplete="off"
            {...register('firstName')}
            error={errors.firstName?.message}
          />
          <FormField
            id="invite-last-name"
            label="Nom"
            autoComplete="off"
            {...register('lastName')}
            error={errors.lastName?.message}
          />
        </div>
        <FormField
          id="invite-email"
          type="email"
          label="Email"
          placeholder="prenom.nom@entreprise.fr"
          autoComplete="off"
          {...register('email')}
          error={errors.email?.message}
        />

        <fieldset className="flex flex-col gap-2">
          <legend className="text-xs font-medium text-zinc-700 mb-1">Profils et périmètre</legend>
          <AccessEditor value={access} onChange={setAccess} disabled={invite.isPending} idPrefix="invite" />
          {accessError ? <p className="text-xs text-red-600">{accessError}</p> : null}
        </fieldset>
      </div>

      <footer className="px-6 py-3 border-t border-zinc-100 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-9 px-3 text-sm text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={invite.isPending}
          className="h-9 px-4 text-sm font-medium bg-zinc-900 text-white rounded-md hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {invite.isPending ? 'Envoi…' : "Envoyer l'invitation"}
        </button>
      </footer>
    </form>
  );
};
